import dayjs from "dayjs"
import * as Sentry from "@sentry/react";
import { InstagramAdPlacement, InstagramPost } from "modules/Types";
import { create } from "zustand";

/** Interface for Facebook SDK window object */
interface FacebookSDK {
    login: (callback: (response: FacebookLoginResponse) => void, options: FacebookLoginOptions) => void;
    api: (path: string, method: string, params: any, callback: (response: any) => void) => void;
    getAuthResponse: () => FacebookAuthResponse | null;
    getLoginStatus: (callback: (response: any) => void, immediate?: boolean) => void;
    getAccessToken: () => string | null;
    Event: {
        subscribe: (event: string, callback: (response: any) => void) => void;
        unsubscribe: (event: string) => void;
    };
    logout: (callback: (response: any) => void) => void;
}

/** Interface for Facebook login response */
interface FacebookLoginResponse {
    authResponse: FacebookAuthResponse | null;
    status?: string;
}

/** Interface for Facebook auth response */
interface FacebookAuthResponse {
    accessToken: string;
    userID: string;
    expiresIn: number;
    signedRequest: string;
}

/** Interface for Facebook login options */
interface FacebookLoginOptions {
    scope: string;
    auth_type?: string;
}

/** Interface for Facebook permission status */
export interface FacebookPermission {
    permission: string;
    status: string;
}

/** Interface for Facebook API params */
interface FacebookApiParams {
    access_token?: string;
    overrideAccessToken?: boolean;
    forceToken?: string;
    [key: string]: any;
}

/** Interface for Ad Campaign Insights params */
interface AdCampaignInsightsParams {
    ad_account: string;
    fields: string;
    level?: string;
    startDate: string | Date;
    endDate: string | Date;
    campaignIds?: string[] | null;
    adIds?: string[] | null;
    daily?: boolean | null;
    breakdowns?: string | null;
    sort?: string | null;
    limit?: number | null;
}

/** Interface for Ad Campaign Insights object */
interface AdCampaignInsightsObject {
    fields: string;
    level: string;
    time_range: {
        since: string;
        until: string;
    };
    date_preset: string;
    default_summary: boolean;
    time_increment?: number;
    breakdowns?: string;
    sort?: string;
    limit?: number;
    filtering?: string;
}

/** Interface for Ad Item Info props */
interface AdItemInfoProps {
    fields: string;
    ids: string[];
    limit?: number;
    type?: 'campaigns' | 'ads';
}

/** Interface for Ad Account Status */
interface AdAccountStatusType {
    name: string;
    valid: boolean;
}

/** Interface for Ad Account Status Map */
interface AdAccountStatusMap {
    [key: number]: AdAccountStatusType;
}

/** Interface for Instagram API Response */
interface InstagramApiResponse<T> extends FacebookApiResponse {
    data: T[];
    business_discovery?: IgBusinessDiscovery;
    paging?: {
        cursors: {
            after: string | null,
            before: string | null
        }
    }
}

declare global {
    interface Window {
        FB?: FacebookSDK;
        HARDCODED_ACCESS_TOKEN?: string | null;
    }
}

export const FACEBOOK_BUSINESS_BASE_URL = 'https://business.facebook.com';
export const ACCOUNT_QUALITY_URL = `${FACEBOOK_BUSINESS_BASE_URL}/accountquality`;
export const AD_ACCOUNT_SETTINGS_URL = `${FACEBOOK_BUSINESS_BASE_URL}/settings/ad-accounts`;
export const PIXEL_SETTINS_URL = `${FACEBOOK_BUSINESS_BASE_URL}/settings/events-dataset-news`;

let cachedFb: FacebookSDK | null = null;
let fbLoginInProgress = false;


interface FacebookServiceState {
    accessToken: string | null;
    setAccessToken: (token: string | null) => void;
}

/** Central store for Facebook authentication state */
const useFacebookService = create<FacebookServiceState>((set) => ({
    accessToken: null,
    setAccessToken: (token) => set({ accessToken: token }),
}));

/** Class to handle Facebook API interactions */
export class FacebookService {
    private static instance: FacebookService;

    private constructor() { }

    public static getInstance(): FacebookService {
        if (!FacebookService.instance) {
            FacebookService.instance = new FacebookService();
        }
        return FacebookService.instance;
    }

    /** Get the current access token from the store */
    private getAccessToken(): string | null {
        const token = useFacebookService.getState().accessToken;
        if (!token) {
            return null
        }
        return token;
    }

    /** Set the access token in the centralized store */
    public setAccessToken(token: string | null): void {
        useFacebookService.getState().setAccessToken(token);
    }

    /** Make a generic FB API call */
    public async queryFB(path: string, { method = 'get', params = {} as FacebookApiParams }): Promise<any> {
        const token = this.getAccessToken();

        const pulledMethod = method || 'get';
        const pulledParams = { ...params };

        if (!token) {
            if ((HARDCODED_ACCESS_TOKEN || (window.HARDCODED_ACCESS_TOKEN && !pulledParams.access_token))
                && !pulledParams.overrideAccessToken) {
                pulledParams.access_token = HARDCODED_ACCESS_TOKEN || window.HARDCODED_ACCESS_TOKEN || '';
            }

            if (pulledParams.forceToken) {
                pulledParams.access_token = pulledParams.forceToken;
            }
        } else if(pulledParams.forceToken) {
            pulledParams.access_token = pulledParams.forceToken;
        } else {
            pulledParams.access_token = token;
        }

        return new Promise((resolve, reject) => {
            const fb = FB();
            if (!fb) {
                const message = 'FB SDK with issues. Please contact Support.';
                Sentry.captureException(message);
                reject(message);
                return;
            }

            fb?.api(path, pulledMethod, pulledParams, function (response: any) {
                if (!response || response.error) {
                    console.error('Error occurred calling FB query');
                    console.log('response error', response?.error);

                    if (response?.error?.code === 2500) {
                        // refresh auth token
                        loginWithFB(null, '').then(() => {
                            FacebookService.getInstance().queryFB(path, {
                                method: pulledMethod,
                                params: pulledParams
                            }).then(resolve).catch(reject);
                        });
                    } else if (response?.error?.code === 190) {
                        reject();
                    } else {
                        reject(response);
                    }
                } else {
                    resolve(response);
                }
            });
        });
    }

}


const HARDCODED_ACCESS_TOKEN: string | null = null;

const { REACT_APP_SYSTEM_USER_IG_ID, REACT_APP_SYSTEM_USER_ACCESS_TOKEN } = process.env;
const FB_BASE_API_URL = 'https://graph.facebook.com/v20.0';

export function FB(): FacebookSDK | null {
    if (cachedFb !== null && typeof cachedFb !== 'undefined') {
        return cachedFb;
    } else if (window.FB) {
        cachedFb = window.FB;
        return window.FB;
    } else {
        return null;
    }
}

export const FB_AD_ACCOUNT_STATUS: AdAccountStatusMap = {
    1: {
        name: 'active',
        valid: true
    },
    2: {
        name: 'disabled',
        valid: false
    },
    3: {
        name: 'unsettled',
        valid: false
    },
    7: {
        name: 'pending review',
        valid: false
    },
    8: {
        name: 'pending review',
        valid: false
    },
    9: {
        name: 'pending review',
        valid: false
    },
    100: {
        name: 'pending closure',
        valid: false
    },
    101: {
        name: 'temporarily unavailable',
        valid: false
    },
};

export function loginWithFB(authType?: string | null, scope?: string): Promise<FacebookAuthResponse> {
    return new Promise((resolve, reject) => {
        const fb = FB();
        if (!fb) {
            const message = 'FB SDK with issues. Contact Support.';
            console.error('no FB found');
            Sentry.captureException(message);
            reject(new Error(message));
            return;
        }

        const loginData: FacebookLoginOptions = {
            scope: scope || process.env.REACT_APP_FACEBOOK_SCOPE || ''
        };

        if (authType) {
            loginData.auth_type = authType;
        }

        try {
            fb?.login((response: FacebookLoginResponse) => {
                if (response.authResponse) {
                    resolve(response.authResponse);
                } else {
                    const errorMessage = `Facebook login failed: ${response.status || 'User cancelled or did not authorize'}`;
                    console.error(errorMessage, response);

                    reject({
                        message: errorMessage,
                        code: 'FB_AUTH_CANCELLED',
                        response
                    });
                }
            }, loginData);
        } catch (error) {
            const err = error as Error;
            const errorMessage = `Facebook login error: ${err.message || 'Unknown error'}`;
            console.error(errorMessage, err);
            reject({
                message: errorMessage,
                code: 'FB_AUTH_ERROR',
                originalError: err
            });
        }
    });
}

/**
 * returns an array with true or false with the invalid permissions based on access status
 * @param {*} array list of permissions to check for
 */
export async function checkForFBPermissions(permissions: string[], access_token = ''): Promise<boolean | FacebookPermission[]> {
    const permissionsObject: Record<string, boolean> = {};
    permissions.forEach(permission => {
        permissionsObject[permission] = true;
    });

    const declinedItems: FacebookPermission[] = [];
    const checkPermissions = await queryFB('/me/permissions', {
        params: {
            access_token
        },
    });

    if (!checkPermissions || !checkPermissions.data) {
        return false;
    }

    const accountPermissions = checkPermissions.data as FacebookPermission[];
    let allValid = true;

    accountPermissions.forEach(facebookPermission => {
        const { permission, status } = facebookPermission;

        if (typeof permissionsObject[permission] !== 'undefined') {
            if (status !== 'granted') {
                permissionsObject[permission] = false;
                allValid = false;
                declinedItems.push(facebookPermission);
            }
        }
    });

    return allValid ? true : declinedItems;
}

/** Interface for Facebook API Response */
interface FacebookApiResponse<T = any> {
    data?: T;
    error?: {
        code: number;
        message: string;
    };
}


export const queryFB = FacebookService.getInstance().queryFB.bind(FacebookService.getInstance());

export async function queryFbAdCampaignInsights(params: AdCampaignInsightsParams): Promise<any[]> {
    const getData = await new Promise<any[]>(async (resolve, reject) => {
        try {
            const obj: AdCampaignInsightsObject = {
                fields: params.fields,
                level: 'account',
                time_range: {
                    since: dayjs(params.startDate).format('YYYY-MM-DD'),
                    until: dayjs(params.endDate).format('YYYY-MM-DD')
                },
                date_preset: 'maximum',
                default_summary: true,
            };

            if (params.daily) {
                obj.default_summary = false;
                obj.time_increment = 1;
            }

            if (params.breakdowns) {
                obj.breakdowns = params.breakdowns;
            }

            if (params.sort) {
                obj.sort = params.sort;
            }

            if (params.limit) {
                obj.limit = params.limit;
            }

            if (params.campaignIds) {
                obj.filtering = `[{field:"campaign.id",operator:"IN",value: ${JSON.stringify(params.campaignIds)}}]`;
            }
            if (params.adIds) {
                obj.filtering = `[{field:"ad.id",operator:"IN",value: ${JSON.stringify(params.adIds)}}]`;
            }

            const results = await queryFB(`/${params.ad_account}/insights`, {
                params: obj
            });

            resolve(results.data || []);
        } catch (e) {
            console.error('error pulling campaign stats', e);
            reject(e);
        }
    });

    return getData;
}

export async function getFbAdItemInfo(props: AdItemInfoProps): Promise<any[]> {
    const pullerPromises = props.ids.map((itemId) => {
        return new Promise(async (resolve, reject) => {
            let url = `/${itemId}`;
            try {
                const obj: { fields: string; limit?: number } = {
                    fields: props.fields
                };

                if (props.limit) {
                    obj.limit = props.limit;
                }

                if (props.type === 'ads') {
                    url += '/ads';
                }

                const results = await queryFB(url, {
                    params: obj
                });

                if (results.data) {
                    resolve(results.data);
                } else {
                    resolve(results);
                }
            } catch (e) {
                console.error("Error pulling campaign", e);
                resolve({});
            }
        });
    });

    return Promise.all(pullerPromises);
}

/** Props for getInstagramPosts function */
interface GetInstagramPostsProps {
    ig_account: string;
    fields?: string;
    limit?: number;
    sort?: string;
    access_token?: string | null;
    after?: string;
}

export async function getInstagramPosts(props: GetInstagramPostsProps): Promise<{
    data: InstagramPost[],
    paging: {
        cursors: {
            after: string | null,
            before: string | null
        }
    }
}> {
    const {
        ig_account,
        fields = `
            id,ig_id,fb_id,permalink,caption,media_product_type,media_type,media_url,is_shared_to_feed,
            like_count,comments_count,thumbnail_url,timestamp,children{id,ig_id,media_type,media_url,thumbnail_url},username
        `,
        limit = 20,
        sort = `["timestamp_descending"]`,
        access_token,
        after
    } = props;

    if (!ig_account) return { data: [], paging: { cursors: { after: null, before: null } } };

    const igMedia = await new Promise<{
        data: InstagramPost[],
        paging: {
            cursors: {
                after: string | null,
                before: string | null
            }
        }
    }>(async (resolve) => {
        const url = `/${ig_account}/media`;
        try {
            const obj: Record<string, any> = {
                fields: fields,
                access_token,
            };

            if (limit) {
                obj.limit = limit;
            }

            if (sort) {
                obj.sort = sort;
            }

            if (after) {
                obj.after = after;
            }

            const results = await queryFB(url, {
                params: obj
            }) as InstagramApiResponse<InstagramPost>;

            if (results.data) {
                // Ensure we always have a valid paging object with cursors
                const paging = results.paging || { cursors: { after: null, before: null } };
                resolve({ 
                    data: results.data, 
                    paging: {
                        cursors: {
                            after: paging.cursors?.after || null,
                            before: paging.cursors?.before || null
                        }
                    } 
                });
            } else {
                resolve({ data: [], paging: { cursors: { after: null, before: null } } });
            }

        } catch (e) {
            console.error('Error pulling instagram posts', e);
            resolve({ data: [], paging: { cursors: { after: null, before: null } } });
        }
    });

    return igMedia;
}

/** Interface for Instagram business discovery data */
export interface IgBusinessDiscovery {
    id: string;
    name: string;
    username: string;
    followers_count: number;
    media_count: number;
    profile_picture_url: string;
}

export const getIgUserData_api = async (username: string): Promise<IgBusinessDiscovery | null> => {
    const igBizDiscCall = new Promise<IgBusinessDiscovery | null>(async (resolve, _reject) => {
        try {
            const endpoint = `${FB_BASE_API_URL}/${REACT_APP_SYSTEM_USER_IG_ID}`;
            const fields = `business_discovery.username(${username}){id,name,username,followers_count,media_count,profile_picture_url}`;
            const igUserData = await queryFB(endpoint, {
                params: { fields, access_token: REACT_APP_SYSTEM_USER_ACCESS_TOKEN },
            }) as InstagramApiResponse<any>;

            resolve(igUserData.business_discovery || null);
        } catch (e) {
            console.error('Error querying instagram username', e);
            resolve(null);
        }
    });
    const timeout = new Promise<false>((resolve, _reject) => setTimeout(() => resolve(false), 15 * 1000));

    const result = await Promise.race([igBizDiscCall, timeout]);
    if (result) return igBizDiscCall; else return null;
}

export async function getLocales(locales = '') {
    const endpoint = `${FB_BASE_API_URL}/search`;
    const response = await queryFB(endpoint, {
        params: {
            access_token: REACT_APP_SYSTEM_USER_ACCESS_TOKEN,
            type: 'adlocale',
            q: locales,
            limit: 100
        }
    });

    return response;
}

export async function getAvailableAdsForInstagramMedia(mediaUrl: string) {
    if (!mediaUrl) return [];

    try {
        const aspectRatio = await getMediaAspectRatio(mediaUrl!);
        const instagram_positions = getSuitableInstagramPositions(aspectRatio);

        return instagram_positions;
    } catch (e) {
        console.error('Error getting available ads for instagram media', e);
        return [];
    }
}

export function getMediaAspectRatio(mediaUrl: string): Promise<number> {
    return new Promise((resolve, reject) => {
        // Check if it's a video by file extension (including URLs with query parameters)
        const isVideo = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|3gp|m4v)(\?|$)/i.test(mediaUrl);

        if (isVideo) {
            const video = document.createElement('video');
            video.onloadedmetadata = () => {
                const aspectRatio = video.videoWidth / video.videoHeight;
                resolve(aspectRatio);
            };
            video.onerror = () => {
                reject(new Error('Failed to load video'));
            };
            video.src = mediaUrl;
        } else {
            const img = new Image();
            img.onload = () => {
                const aspectRatio = img.width / img.height;
                resolve(aspectRatio);
            };
            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };
            img.src = mediaUrl;
        }
    });
}

export function getSuitableInstagramPositions(aspectRatio: number): Array<InstagramAdPlacement> {
    const suitablePositions: Array<InstagramAdPlacement> = [];

    // Instagram feed ads (stream, explore, explore_home, profile_feed, ig_search)
    // Aspect ratio must be between 4:5 (0.8) and 1.91:1 (1.91) inclusively
    if (aspectRatio >= 0.8 && aspectRatio <= 1.91) {
        suitablePositions.push('stream', 'explore', 'explore_home', 'profile_feed', 'ig_search');
    }

    // Instagram story and reels ads
    // Aspect ratio must be 9:16 (0.5625) for both
    if (Math.abs(aspectRatio - 0.5625) < 0.01) { // Allow small tolerance for floating point precision
        suitablePositions.push('story', 'reels', 'profile_reels');
    }

    return suitablePositions;
}

export function getFBAuthResponse(): FacebookAuthResponse | null {
    const fb = FB();
    if (!fb) {
        const message = 'FB SDK with issues. Contact Support.';
        Sentry.captureException(message);
        return null;
    }

    const p = fb?.getAuthResponse();
    return p
}

interface FacebookLoginError {
    message: string;
    code: 'FB_AUTH_CANCELLED' | 'FB_AUTH_ERROR';
    response?: FacebookLoginResponse;
    originalError?: Error;
}

export default FB